/**
 * @fileoverview Own the browser PDF rendering lifecycle for an authorized document source. `pdfRuntime.js` supplies shared PDF.js/worker initialization; the viewer owns loading, page/zoom state, and render cleanup. Source selection and authorization belong to the workspace/backend. Navigating PDF pages does not change document revision or cached metadata identity.
 */

import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import './PdfDocumentViewer.css';



export default function PdfDocumentViewer({ src, title, anchor = null }) {
  const [url, hash = ''] = src.split('#');
  const hashPage = Number(new URLSearchParams(hash).get('page'));
  const requestedPage = Number.isFinite(hashPage) ? Math.max(1, Math.floor(hashPage)) : 1;
  // A new file must not briefly display pages from the previous version.
  return <PdfViewer key={url} url={url} title={title} requestedPage={requestedPage} anchor={anchor} />;
}



function PdfViewer({ url, title, requestedPage, anchor }) {
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(requestedPage);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const viewportRef = useRef(null);
  const pagesRef = useRef(null);
  const viewerRef = useRef(null);
  const requestedPageRef = useRef(requestedPage);
  const searchRef = useRef(null);
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const [anchorStatus, setAnchorStatus] = useState('');

  useEffect(

  () => {
    if (!pageCount) return;
    const eventBus = searchRef.current;
    if (!anchor?.quote) {
      eventBus?.dispatch('findbarclose', {});
      setAnchorStatus('');
      return;
    }
    // Context disambiguates repeated words. PDF.js handles text split across
    // runs/lines, ligatures and soft hyphenation without flattening the page.
    const query = `${anchor.prefix || ''}${anchor.quote}${anchor.suffix || ''}`.replace(/\s+/g, ' ').trim();
    setAnchorStatus('Finding comment passage…');
    eventBus?.dispatch('find', { source: eventBus, type: '', query,
      caseSensitive: true, entireWord: false, highlightAll: true, findPrevious: false, matchDiacritics: true });
  }, [anchor, pageCount]);

  useEffect(() => {
    requestedPageRef.current = requestedPage;
    const viewer = viewerRef.current;
    if (viewer?.pagesCount) viewer.currentPageNumber = Math.min(requestedPage, viewer.pagesCount);
  }, [requestedPage]);

  useEffect(

  () => {
    const controller = new AbortController();
    let task;
    let viewer;
    let resizeObserver;
    setLoading(true);
    setError('');
    setPageCount(0);
    const fail = 

    (message) => {
      if (!controller.signal.aborted) { setError(message); setLoading(false); }
    };
    Promise.all([
      import('./pdfRuntime'),
      // src is already a browser-ready URL, including /api in deployed builds.
      api.get(url, { baseURL: '', responseType: 'arraybuffer', signal: controller.signal, timeout: 30000 }),
    ]).then(

    async ([runtime, response]) => {
      if (controller.signal.aborted) return;
      const eventBus = new runtime.EventBus();
      const linkService = new runtime.PDFLinkService({ eventBus });
      const findController = new runtime.PDFFindController({ eventBus, linkService });
      searchRef.current = eventBus;
      eventBus.on('updatefindcontrolstate', 

      ({ state }) => {
        if (controller.signal.aborted || !anchorRef.current?.quote) return;
        if (state === 1) setAnchorStatus('Passage not found in this preview. See the quoted text in the comment.');
        else if (state === 0 || state === 2) setAnchorStatus('Comment passage highlighted');
      });
      eventBus.on('updatefindmatchescount', ({ matchesCount }) => {
        if (!controller.signal.aborted && anchorRef.current?.quote && matchesCount.total > 1) setAnchorStatus(`${matchesCount.total} matching passages highlighted`);
      });
      viewer = new runtime.PDFViewer({
        container: viewportRef.current,
        viewer: pagesRef.current,
        eventBus,
        linkService,
        findController,
        // Keep a continuous layout, rendering nearby pages on demand instead
        // of allocating a canvas for every page of a long document.
        annotationMode: 0,
        abortSignal: controller.signal,
      });
      viewerRef.current = viewer;
      linkService.setViewer(viewer);
      eventBus.on('pagesinit', 

      () => {
        if (controller.signal.aborted) return;
        viewer.currentScaleValue = 'page-width';
        viewer.currentPageNumber = Math.min(requestedPageRef.current, viewer.pagesCount);
        setPageCount(viewer.pagesCount);
      });
      eventBus.on('pagechanging', ({ pageNumber }) => {
        if (!controller.signal.aborted) setPage(pageNumber);
      });
      eventBus.on('scalechanging', ({ scale: nextScale }) => {
        if (!controller.signal.aborted) setScale(nextScale);
      });
      eventBus.on('pagerendered', ({ error: renderError }) => {
        if (renderError) fail('Could not render this PDF page.');
        else if (!controller.signal.aborted) setLoading(false);
      });
      resizeObserver = new ResizeObserver(() => {
        if (!controller.signal.aborted && viewer.pagesCount) {
          // Preserve reading position on resize and leave explicit zoom alone.
          if (viewer.currentScaleValue === 'page-width') viewer.currentScaleValue = 'page-width';
          viewer.update();
        }
      });
      resizeObserver.observe(viewportRef.current);
      task = runtime.getDocument({ data: new Uint8Array(response.data), isEvalSupported: false });
      const pdf = await task.promise;
      if (controller.signal.aborted) return;
      linkService.setDocument(pdf);
      viewer.setDocument(pdf);
      viewer.pagesPromise.catch(() => fail('Could not render this PDF page.'));
    }).catch(() => fail('Could not load the PDF.'));
    return   () => {
      controller.abort();
      resizeObserver?.disconnect();
      viewer?.setDocument(null);
      viewerRef.current = null;
      searchRef.current = null;
      task?.destroy();
    };
  }, [url, attempt]);

  const goToPage = 

  (nextPage) => {
    if (viewerRef.current?.pagesCount) viewerRef.current.currentPageNumber = nextPage;
  };
  const zoom = 

  (amount) => {
    const viewer = viewerRef.current;
    if (viewer?.pagesCount) viewer.currentScale = Math.max(0.25, Math.min(4, viewer.currentScale + amount));
  };

  return (
    <section className="kc-pdf-viewer" aria-label={title}>
      <div className="kc-pdf-toolbar" aria-label="PDF controls">
        <button type="button" aria-label="Previous PDF page" disabled={!pageCount || page <= 1} onClick={() => goToPage(page - 1)}>‹</button>
        <span>Page {page} of {pageCount || '…'}</span>
        <button type="button" aria-label="Next PDF page" disabled={!pageCount || page >= pageCount} onClick={() => goToPage(page + 1)}>›</button>
        <button type="button" aria-label="Zoom out" disabled={!pageCount || scale <= 0.25} onClick={() => zoom(-0.25)}>−</button>
        <button type="button" disabled={!pageCount} onClick={() => { viewerRef.current.currentScaleValue = 'page-width'; }}>Fit width</button>
        <button type="button" aria-label="Zoom in" disabled={!pageCount || scale >= 4} onClick={() => zoom(0.25)}>+</button>
        <a href={url} target="_blank" rel="noreferrer">Open PDF</a>
      </div>
      {anchorStatus && <div className="kc-pdf-message" role="status">{anchorStatus}</div>}
      {error ? <div className="kc-pdf-message" role="alert">{error} <button type="button" onClick={() => setAttempt(attempt + 1)}>Retry PDF</button></div>
        : loading && <div className="kc-pdf-message" role="status">Loading PDF…</div>}
      <div className="kc-pdf-body">
        <div className="kc-pdf-viewport" ref={viewportRef} tabIndex={0} role="region" aria-label="PDF pages" aria-busy={loading}>
          <div className="pdfViewer" ref={pagesRef} />
        </div>
      </div>
    </section>
  );
}
